import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReferenceImageUploader } from '../../shared';
import { ReferenceImage } from '../../../services/imageGenerationApi';
import { LibraryItem } from '../../../types';
import VideoImageSourceSelector from './VideoImageSourceSelector';
import VideoPromptInput from './VideoPromptInput';
import VideoOptionsGrid from './VideoOptionsGrid';
import VideoAdvancedControls from './VideoAdvancedControls';
import VideoGenerateButton from './VideoGenerateButton';
import VeoVideoControls from './VeoVideoControls';

interface VideoTabProps {
  // Image source management
  videoImageSource: 'generated' | 'library' | 'upload';
  setVideoImageSource: (source: 'generated' | 'library' | 'upload') => void;
  
  // Generated images
  generatedImages: Array<{
    url: string;
    prompt: string;
    seed: number;
    index: number;
    provider?: 'runway' | 'sdxl' | 'banana_nano';
    loading?: boolean;
  }> | null;
  setGeneratedImages: (images: any) => void;
  selectedImageIndex: number;
  
  // Reference images (library)
  referenceImages: ReferenceImage[];
  addReferenceImage: (image: ReferenceImage) => void;
  handleRemoveReferenceImage: (index: number) => void;
  addFromLibrary: (item: LibraryItem) => void;
  canAddMore: boolean;
  
  // Video generation
  videoPrompt: string;
  setVideoPrompt: (prompt: string) => void;
  videoDuration: number;
  setVideoDuration: (duration: number) => void;
  videoQuality: string;
  setVideoQuality: (quality: string) => void;
  videoAspectRatio: string;
  setVideoAspectRatio: (ratio: string) => void;
  selectedVideoProvider: 'runway' | 'ltx' | 'veo';
  setSelectedVideoProvider: (provider: 'runway' | 'ltx' | 'veo') => void;
  
  // Advanced controls (Runway)
  selectedMotionTemplate: string;
  setSelectedMotionTemplate: (template: string) => void;
  selectedMotionTypes: string[];
  setSelectedMotionTypes: (types: string[]) => void;
  motionIntensity: string;
  setMotionIntensity: (intensity: string) => void;
  motionSpeed: number;
  setMotionSpeed: (speed: number) => void;
  
  // Veo-specific props (optional - will be passed from parent when provider is veo)
  veoModel?: string;
  onVeoModelChange?: (model: string) => void;
  dialogueCue?: string;
  onDialogueCueChange?: (value: string) => void;
  veoSoundEffects?: string;
  onVeoSoundEffectsChange?: (value: string) => void;
  veoAmbientNoise?: string;
  onVeoAmbientNoiseChange?: (value: string) => void;
  veoAudioStyle?: string;
  onVeoAudioStyleChange?: (value: string) => void;
  veoVisualStyle?: string;
  onVeoVisualStyleChange?: (value: string) => void;
  veoLighting?: string;
  onVeoLightingChange?: (value: string) => void;
  veoCameraMovement?: string;
  onVeoCameraMovementChange?: (value: string) => void;
  veoMotionIntensity?: string;
  onVeoMotionIntensityChange?: (value: string) => void;
  veoMood?: string;
  onVeoMoodChange?: (value: string) => void;
  personGeneration?: string;
  onPersonGenerationChange?: (value: string) => void;
  negativePrompt?: string;
  onNegativePromptChange?: (value: string) => void;
  
  // Veo accordion states
  showVeoAudioAccordion?: boolean;
  onToggleVeoAudioAccordion?: () => void;
  showVeoVisualAccordion?: boolean;
  onToggleVeoVisualAccordion?: () => void;
  showVeoMotionAccordion?: boolean;
  onToggleVeoMotionAccordion?: () => void;
  showVeoSafetyAccordion?: boolean;
  onToggleVeoSafetyAccordion?: () => void;
  
  // Optimization
  isOptimizing: boolean;
  optimizationResult: any;
  showOptimizationResult: boolean;
  setShowOptimizationResult: (show: boolean) => void;
  handleAutoOptimize: () => void;
  applyOptimizedParams: () => void;
  copyOptimizationResult: () => void;
  
  // Accordions (Runway)
  showVideoMotionAccordion: boolean;
  setShowVideoMotionAccordion: (show: boolean) => void;
  
  // Generation
  isGeneratingVideo: boolean;
  handleGenerateVideo: () => void;
  getSelectedVideoImage: () => any;
  
  // Other props
  projectId?: string;
  isAdmin: boolean;
  setActiveTab: (tab: 'image' | 'video' | 'prompt') => void;
  onCancel?: () => void;
}

const VideoTab: React.FC<VideoTabProps> = ({
  videoImageSource,
  setVideoImageSource,
  generatedImages,
  setGeneratedImages,
  selectedImageIndex,
  referenceImages,
  addReferenceImage,
  handleRemoveReferenceImage,
  addFromLibrary,
  canAddMore,
  videoPrompt,
  setVideoPrompt,
  videoDuration,
  setVideoDuration,
  videoQuality,
  setVideoQuality,
  videoAspectRatio,
  setVideoAspectRatio,
  selectedVideoProvider,
  setSelectedVideoProvider,
  selectedMotionTemplate,
  setSelectedMotionTemplate,
  selectedMotionTypes,
  setSelectedMotionTypes,
  motionIntensity,
  setMotionIntensity,
  motionSpeed,
  setMotionSpeed,
  
  // Veo props
  veoModel,
  onVeoModelChange,
  dialogueCue,
  onDialogueCueChange,
  veoSoundEffects,
  onVeoSoundEffectsChange,
  veoAmbientNoise,
  onVeoAmbientNoiseChange,
  veoAudioStyle,
  onVeoAudioStyleChange,
  veoVisualStyle,
  onVeoVisualStyleChange,
  veoLighting,
  onVeoLightingChange,
  veoCameraMovement,
  onVeoCameraMovementChange,
  veoMotionIntensity,
  onVeoMotionIntensityChange,
  veoMood,
  onVeoMoodChange,
  personGeneration,
  onPersonGenerationChange,
  negativePrompt,
  onNegativePromptChange,
  showVeoAudioAccordion,
  onToggleVeoAudioAccordion,
  showVeoVisualAccordion,
  onToggleVeoVisualAccordion,
  showVeoMotionAccordion,
  onToggleVeoMotionAccordion,
  showVeoSafetyAccordion,
  onToggleVeoSafetyAccordion,
  
  // Other props
  isOptimizing,
  optimizationResult,
  showOptimizationResult,
  setShowOptimizationResult,
  handleAutoOptimize,
  applyOptimizedParams,
  copyOptimizationResult,
  showVideoMotionAccordion,
  setShowVideoMotionAccordion,
  isGeneratingVideo,
  handleGenerateVideo,
  getSelectedVideoImage,
  projectId,
  isAdmin,
  setActiveTab,
  onCancel
}) => {
  const { t } = useTranslation('aiGenerator');
  
  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#8b5cf6', fontSize: '16px' }}>{t('video.title')}</h4>
        
        {/* Library Images - Direct integration */}
        <div style={{ marginBottom: '20px', backgroundColor: '#1a1a1a', padding: '15px', borderRadius: '8px' }}>
          <h4 style={{ margin: '0 0 15px 0', color: '#8b5cf6', fontSize: '14px' }}>{t('video.selectImageTitle')}</h4>
          <ReferenceImageUploader
            referenceImages={referenceImages}
            onAddReferenceImage={(image) => {
              addReferenceImage(image);
              // Automatically switch to library source when an image is added
              setVideoImageSource('library');
            }}
            onRemoveReferenceImage={handleRemoveReferenceImage}
            onAddFromLibrary={(item) => {
              addFromLibrary(item);
              // Automatically switch to library source when an image is added
              setVideoImageSource('library');
            }}
            canAddMore={canAddMore}
            maxImages={1}
            projectId={projectId}
          />
          {referenceImages.length === 0 && generatedImages === null && (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center',
              border: '2px dashed rgba(139, 92, 246, 0.3)',
              borderRadius: '6px',
              backgroundColor: 'rgba(139, 92, 246, 0.05)'
            }}>
              <div style={{ color: '#8b5cf6', fontSize: '14px', marginBottom: '8px' }}>
                {t('video.noImageSelected')}
              </div>
              <div style={{ color: '#999', fontSize: '12px' }}>
                {t('video.uploadOrBrowse')}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Show generated image if coming from image tab */}
      {generatedImages && (
        <div style={{ marginBottom: '20px', backgroundColor: '#1a1a1a', padding: '15px', borderRadius: '8px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#8b5cf6', fontSize: '14px' }}>Source Image</h4>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <img 
              src={generatedImages[selectedImageIndex].url}
              alt="Source Image"
              style={{
                width: '120px',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '6px',
                border: '2px solid #8b5cf6'
              }}
            />
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#ccc' }}>
                <strong>Selected:</strong> Option {selectedImageIndex + 1} (Seed: {generatedImages[selectedImageIndex].seed})
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#ccc' }}>
                <strong>Prompt:</strong> {generatedImages[selectedImageIndex].prompt}
              </p>
              <p style={{ margin: '0', fontSize: '12px', color: '#999' }}>
                This image will be used to generate your video
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Video Controls - Always show */}
        <VideoPromptInput
            videoPrompt={videoPrompt}
            setVideoPrompt={setVideoPrompt}
          />

          <VideoOptionsGrid
            videoDuration={videoDuration}
            setVideoDuration={setVideoDuration}
            videoQuality={videoQuality}
            setVideoQuality={setVideoQuality}
            videoAspectRatio={videoAspectRatio}
            setVideoAspectRatio={setVideoAspectRatio}
            selectedVideoProvider={selectedVideoProvider}
            setSelectedVideoProvider={setSelectedVideoProvider}
            isAdmin={isAdmin}
          />

          {/* Provider-specific controls */}
          {selectedVideoProvider === 'veo' ? (
            <VeoVideoControls
              veoModel={veoModel || 'veo-3.0-generate-preview'}
              onVeoModelChange={onVeoModelChange || (() => {})}
              dialogueCue={dialogueCue || ''}
              onDialogueCueChange={onDialogueCueChange || (() => {})}
              soundEffects={veoSoundEffects || ''}
              onSoundEffectsChange={onVeoSoundEffectsChange || (() => {})}
              ambientNoise={veoAmbientNoise || ''}
              onAmbientNoiseChange={onVeoAmbientNoiseChange || (() => {})}
              audioStyle={veoAudioStyle || ''}
              onAudioStyleChange={onVeoAudioStyleChange || (() => {})}
              visualStyle={veoVisualStyle || ''}
              onVisualStyleChange={onVeoVisualStyleChange || (() => {})}
              lighting={veoLighting || ''}
              onLightingChange={onVeoLightingChange || (() => {})}
              cameraMovement={veoCameraMovement || ''}
              onCameraMovementChange={onVeoCameraMovementChange || (() => {})}
              motionIntensity={veoMotionIntensity || 'moderate'}
              onMotionIntensityChange={onVeoMotionIntensityChange || (() => {})}
              mood={veoMood || ''}
              onMoodChange={onVeoMoodChange || (() => {})}
              personGeneration={personGeneration || 'allow_adult'}
              onPersonGenerationChange={onPersonGenerationChange || (() => {})}
              negativePrompt={negativePrompt || ''}
              onNegativePromptChange={onNegativePromptChange || (() => {})}
              showVeoAudioAccordion={showVeoAudioAccordion || false}
              onToggleVeoAudioAccordion={onToggleVeoAudioAccordion || (() => {})}
              showVeoVisualAccordion={showVeoVisualAccordion || false}
              onToggleVeoVisualAccordion={onToggleVeoVisualAccordion || (() => {})}
              showVeoMotionAccordion={showVeoMotionAccordion || false}
              onToggleVeoMotionAccordion={onToggleVeoMotionAccordion || (() => {})}
              showVeoSafetyAccordion={showVeoSafetyAccordion || false}
              onToggleVeoSafetyAccordion={onToggleVeoSafetyAccordion || (() => {})}
            />
          ) : (
            <VideoAdvancedControls
              selectedVideoProvider={selectedVideoProvider}
              isOptimizing={isOptimizing}
              optimizationResult={optimizationResult}
              showOptimizationResult={showOptimizationResult}
              setShowOptimizationResult={setShowOptimizationResult}
              handleAutoOptimize={handleAutoOptimize}
              applyOptimizedParams={applyOptimizedParams}
              copyOptimizationResult={copyOptimizationResult}
              showVideoMotionAccordion={showVideoMotionAccordion}
              setShowVideoMotionAccordion={setShowVideoMotionAccordion}
              selectedMotionTemplate={selectedMotionTemplate}
              setSelectedMotionTemplate={setSelectedMotionTemplate}
              selectedMotionTypes={selectedMotionTypes}
              setSelectedMotionTypes={setSelectedMotionTypes}
              motionIntensity={motionIntensity}
              setMotionIntensity={setMotionIntensity}
              motionSpeed={motionSpeed}
              setMotionSpeed={setMotionSpeed}
              videoPrompt={videoPrompt}
            />
          )}

          <VideoGenerateButton
            isGeneratingVideo={isGeneratingVideo}
            handleGenerateVideo={handleGenerateVideo}
            videoPrompt={videoPrompt}
            setActiveTab={setActiveTab}
            onCancel={onCancel}
          />


    </>
  );
};

export default VideoTab;